import RenderManager from "../rendering/render-manager";
import Utils from "../utils";

import getStyle from "../utils/get-style";
import addControls from "../utils/add-controls";
import addSlideControls from "../slides/add-slide-controls";
import addBackButton from "../slides/back-button";
import getRelativeCoordinates from "../utils/get-relative-coordinates";

import { addInteractivity } from "../interactivity/add-interactivity";
import { initAnimation } from "../utils/add-animation";
import { resize, checkRemActualSize } from "../utils/resize";
import { listen, fire } from "../utils/events"
import viewAll from '../../editor/helpers/view-all.js';

import { Loader as PassByRaycasterLoader } from '../utils/passby-raycaster';
import cloneObject from "../../editor/helpers/cloneObject";
import QuizManager from "../quizes/quiz-manager";
import SlideManager from "../utils/slide-manager";

import SetterGetterQ3Data from "../pivots/setter-getter-q3-data.js";

function setScene(editing, LimitsCtl) {

    const passByRaycasterLoader = createPassByRaycasterLoader();
    const style = getStyle();
    // const rData = Q3.data.slides[0].renderer;

    let debugCanvas;

    if (Utils.queryParams.has("debug_canvas")) {
        debugCanvas = document.createElement("canvas");
        debugCanvas.id = "debug_canvas";

        debugCanvas.style.transform = "scale(1, -1)";
        debugCanvas.style.filter = "FlipV";
        debugCanvas.style.pointerEvents = "none";
        debugCanvas.style.position = "absolute";
        debugCanvas.style.zIndex = "9999";

        document.getElementById("viewer").appendChild(debugCanvas);
    }

    RenderManager.root.add(Q3.labels.live.regionOverlay);

    const supports = Q3.browserCapabilities.supports;
    const capabilities = supports.postProcessing;
    const hasTAA = capabilities.taa.enabled && capabilities.taa.samples > 0;
    const hasSSAA = capabilities.ssaa.enabled && capabilities.ssaa.samples > 0;
    const hasFXAA = capabilities.fxaa.enabled;

    RenderManager.init({
        localClippingEnabled: true,
        alpha: true,
        antialias: !hasTAA && hasSSAA && !hasFXAA && supports.browserAA.enabled,
        preserveDrawingBuffer: true,
        logarithmicDepthBuffer: true /* logarithmic to false for the  linear calculation of label and controls"target distance*/
        // // logarithmicDepthBuffer: false /* lets disable it for now, we don"t need it for such small scenes that we have and its not working with ortho camera and because of this directional shadows are not working */
    }, {
        enabled: !!Utils.queryParams.has("disable_raycasting"),
        buildMeshRows: true,
        delay: 24,
        dynamicDelay: {
            enabled: Utils.queryParams.has("dynamic_raycasting"),
            min: 0,
            max: 30,
            allowedDelay: 1000 / 60
        },
        debugCanvas,
        scale: 1 / 4
    }, Q3.data.setups.camera, passByRaycasterLoader);

    RenderManager.setSize(style.width, style.height);

    // const left = rData.left * style.width;
    // const top = rData.top * style.height;
    // const width = rData.width * style.width;
    // const height = rData.height * style.height;

    // RenderManager.setViewport(left, top, width, height);
    // RenderManager.setScissor(left, top, width, height);

    RenderManager.shadowMap.enabled = supports.shadows.enabled;
    RenderManager.shadowMap.autoUpdate = false;
    RenderManager.shadowMap.needsUpdate = true;

    if (Q3.data.setups.hasOwnProperty("toneMapping")) RenderManager.toneMapping = Q3.data.setups.toneMapping;
    if (Q3.data.setups.hasOwnProperty("toneMappingExposure")) RenderManager.toneMappingExposure = Q3.data.setups.toneMappingExposure;
    if (Q3.data.setups.hasOwnProperty("gammaInput")) RenderManager.gammaInput = Q3.data.setups.gammaInput;
    if (Q3.data.setups.hasOwnProperty("gammaOutput")) RenderManager.gammaOutput = Q3.data.setups.gammaOutput;

    //prevent using orbitcontrols if mouse outside the viewport
    Q3.container.parentElement.addEventListener("mousemove", disableCanvasWhenOutside);

    //this isn"t fired (enough??)???
    Q3.container.parentElement.addEventListener("touchmove", disableCanvasWhenOutside);
    Q3.container.parentElement.addEventListener("touchstart", disableCanvasWhenOutside);

    // in the past background was global so if we have an old tour then copy this
    // setting to all slides that do not have their own definition of background
    if (Q3.data.setups.background.type) {
        for (let i = 0; i < Q3.data.slides.length; i++) {
            const slide = Q3.data.slides[i];
            if (!slide.background.type) {
                slide.background.type = Q3.data.setups.background.type;
                slide.background.value = cloneObject(Q3.data.setups.background.value);
            }
        }
    }

    const pivotSetterGetter = new SetterGetterQ3Data(Q3.data);

    addControls(LimitsCtl, passByRaycasterLoader, pivotSetterGetter);
    addInteractivity(passByRaycasterLoader, pivotSetterGetter);

    window.addEventListener("scroll", resize);
    if (!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        window.addEventListener("resize", resize);
    }

    function orientationChanged() {
        const frames = 60;
        return new window.Promise(function (resolve) {
            const go = (i, height0) => {
                window.innerHeight !== height0 || i >= frames ?
                    resolve() :
                    window.requestAnimationFrame(() => go(i + 1, height0));
            };
            go(0, window.innerHeight);
        });
    }
    window.addEventListener("orientationchange", (e) => {
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        const onResize = () => {
            resize(e);
            orientationChanged().then(() => {
                resize(e);
            })
            window.removeEventListener("resize", onResize);
        }
        window.addEventListener("resize", onResize);
    });

    resize();

    RenderManager.deferredSetHighlightObjects(); // highlight pass is not necessarily initialized yet

    initAnimation();

    if (Q3.editMode) RenderManager.doRender(0);

    if (!editing) {
        listen("loaded", () => {
            resize();
            checkRemActualSize();
        });
    }

    listen("splashremoved", async () => {
        requestAnimationFrame(t => RenderManager.doRender(t));
        fire("commit_slide", { noAnimation: true });

        addSlideControls();
        addBackButton();

        fire("tourReady");
        console.log("Tour is fully initialized.");

        if (Q3.isAssetMode) viewAll(true);

        RenderManager.activeScene.traverse(o => {
            if (o.needsInit) o.init();
        });

        Q3.isReady = true;

        Q3.labels.live._renderLabels();

        if (Utils.queryParams.get("callouts") === "true") {
            document.body.classList.add("callouts-mode");
            Q3.labels.live.setGlobalVisibility("callouts");
            Q3.RenderManager.activeCamera.update = true;
        }
        // const slideIndex = SlideManager.activeSlide;
        // if (!Q3.editMode && QuizManager.isQuiz3D(slideIndex))
        //     QuizManager.initSlideQuiz(slideIndex);
    });
}

function createPassByRaycasterLoader() {
    const loader = new PassByRaycasterLoader();

    loader.resumeLoading(Date.now() + Q3.editMode ? 20 : 5);

    Q3.AssetLibrary.events.addEventListener('add', ({ assetType, uuid, asset }) => {
        if (assetType === 'geometries') {
            loader.addGeometry(asset);
        }
    });
    Q3.AssetLibrary.events.addEventListener('remove', ({ assetType, uuid, asset }) => {
        if (assetType === 'geometries') {
            loader.removeGeometry(asset);
        }
    });

    return loader;
}

export default setScene;
export { setScene };

function disableCanvasWhenOutside(e) {
    if (!Q3.layers || !Q3.layers.list || Q3.layers.list["Renderer"].visible === false) return;

    const crd = getRelativeCoordinates(e, Q3.container);//, style = getStyle();
    const vp = RenderManager.getViewPort();
    const top = vp.top, left = vp.left, bottom = vp.bottom, right = vp.right;
    const mouseIsOutside = crd.x < left || crd.x > right || crd.y > bottom || crd.y < top;

    RenderManager.domElement.className = mouseIsOutside ? "disabled" : "";
}
